/**
 * Sorts the given array of objects by the id member of the objects.
 * @param objectsArr The array of objects to sort.
 * @param sortDescending Determines of the array should be sorted descending.
 */
export function sortById(objectsArr: any[], sortDescending = false): void {
  objectsArr.sort((objA, objB) => {
    if (objA.id < objB.id) {
      return -1;
    }
    if (objA.id > objB.id) {
      return 1;
    }
    return 0;
  });

  if (sortDescending) {
    objectsArr.reverse();
  }
}

/**
 * Compares 2 values being equal
 * Arrays and Objects will not be deep compared
 * Order of elements/keys in Arrays/Objects doesn't matter
 * undefined / null values will also be handled but strictly
 * @param a
 * @param b
 */
export function valuesEqual(a: string | unknown[] | object, b: string | unknown[] | object): boolean {
  if (typeof a !== typeof b) {
    return false;
  } else if (a === b) {
    return true;
  } else if (!a || !b) {
    return a === b;
  } else if (Array.isArray(a) && Array.isArray(b)) {
    return arraysAreEqual(a, b, false);
  } else if (typeof a === 'object' && typeof b === 'object') {
    const keys = Object.keys(a);
    if (keys.length === Object.keys(b).length) {
      return keys.every(key => a[key] === b[key]);
    }
  }
  return false;
}

/**
 * Compares 2 arrays to check if they are equal
 * It will not compare complex items deeply
 * @param a
 * @param b
 * @param strictArrayOrder
 */
export function arraysAreEqual<T>(a: T[], b: T[], strictArrayOrder = true): boolean {
  if (Array.isArray(a) && Array.isArray(b) && a.length === b.length) {
    if (strictArrayOrder) {
      return a.every((entry, index) => b[index] === entry);
    }
    return a.every(entry => b.includes(entry));
  }
  return false;
}

/**
 * Converts an object to Formdata
 * @param obj
 */
export function objToFormData(obj: object): FormData {
  const formData = new FormData();
  Object.entries(obj).forEach(([key, val]) => {
    if (typeof val !== 'undefined') {
      formData.append(key, `${val}`);
    }
  });
  return formData;
}

/**
 * Compares 2 objects by JSON conversion
 * This usually works well but can fail in case
 * Object keys and Array values are on different indexes
 * @param a
 * @param b
 */
export function jsonEqual<T = unknown>(a: T, b: T): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * inverts an object by swapping key / values
 * doesn't take care for nested objects
 * @param obj
 */
export function invertObject(obj: object): object {
  const inverted = {};
  Object.keys(obj).forEach(key => {
    inverted[obj[key]] = key;
  });
  return inverted;
}

export function isEmptyObject(obj: object): boolean {
  return obj && Object.keys(obj).length === 0;
}

/**
 * Removes empty properties from an Object (nullish or empty strings, objects or arrays)
 * Nested objects also would be cleaned but not nested arrays.
 * @param data
 */
export function removeEmptyObjectProps<T = Record<string, unknown>>(data: T): T {
  return Object.keys(data).reduce((acc, key) => {
    const dataVal = data[key];
    const isObj = isObject(dataVal);
    const value = isObj ? removeEmptyObjectProps(dataVal) : dataVal;
    const isEmpty = isObj && isEmptyObject(value);
    const isEmptyArray = Array.isArray(value) && !value.length;
    if (value === undefined || value === null || value === '' || isEmpty || isEmptyArray) {
      return acc;
    }
    return { ...acc, [key]: value };
  }, {} as T);
}

/**
 * Returns if value is an object.
 * Always returns false for null, function or array
 * @param val
 */
export function isObject(val: unknown): val is Record<string, unknown> {
  if (val === null || typeof val === 'function' || Array.isArray(val)) {
    return false;
  }
  return typeof val === 'object';
}

/**
 * helper for more eslint-compliant hasOwnProperty check
 * @param obj
 * @param prop
 */
export function hasOwnProp<T = Record<string, unknown>>(obj: T, prop: keyof T): boolean {
  return !!obj && Object.prototype.hasOwnProperty.call(obj, prop);
}

/**
 * Stringify data containing circular structures, as without,
 * such data will cause `JSON.stringify()` to bail out.
 * @param data
 */
export function circularSafeStringify(data: unknown): string {
  return JSON.stringify(
    data,
    (() => {
      const cache = new WeakSet();
      return (key, value) => {
        if (typeof value === 'object' && value !== null) {
          if (cache.has(value)) {
            return '[Circular]';
          }
          cache.add(value);
        }
        return value;
      };
    })()
  );
}

/**
 * Pick subset from an object
 * @param obj
 * @param keys
 */
export function pickSubset<O extends object = Record<string, unknown>>(obj: O, keys: Array<keyof O>): Partial<O> {
  if (!keys || keys.length === 0) {
    return obj;
  }
  return keys
    .filter(key => key in obj)
    .reduce<Partial<O>>((obj2, key) => {
      obj2[key] = obj[key];
      return obj2;
    }, {});
}

/**
 * Maps a list of items to a record of items.
 * The record's key is generated from the given item's property value
 *
 * @param list
 * @param itemProperty
 *
 * @example Mapping by `name` property
 * ```ts
 * const items = [{ name: 'alice' }, { name: 'bob }]
 * const result = mapToRecordByProp(items, 'name');
 * console.log(result)
 * ```
 * ```ts
 * // Prints
 * // {
 * //   alice: { name: 'alice' },
 * //   bob: { name: 'bob' }
 * // }
 * ```
 */
export function mapToRecordByProp<T extends { [key in K]?: symbol | string | number }, K extends keyof T>(
  list: T[] | Readonly<T[]>,
  itemProperty: K
): Record<T[K], T> {
  return Object.assign({}, ...list.map(v => ({ [v[itemProperty]]: v })));
}

/**
 *
 * @param object
 * @param omitList the object properties that will be omitted
 *
 * @returns A new object without the given property
 *
 * @example
 * ```ts
 * const object = { name1: 'alice', name2: 'bob }
 * const result = omitProperty(object, 'name1');
 * console.log(result)
 * ```
 * ```ts
 * // Prints
 * // { name2: 'bob }
 * ```
 */
export function omitProperties<T extends object, K extends keyof T>(object: T, ...omitList: K[]): Omit<T, K> {
  return Object.fromEntries(Object.entries(object).filter(([key]) => !omitList.includes(key as K))) as Omit<T, K>;
}
